import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useRealtimeStore } from '../../../shared/realtime/realtimeStore'
import type { ProcurementPeriodKey } from './types'

const PERIODS: { key: ProcurementPeriodKey; label: string }[] = [
  { key: 'day', label: 'День' },
  { key: 'week', label: 'Неделя' },
  { key: 'month', label: 'Месяц' },
]

const MATRIX_RU: Record<string, string> = {
  STOCK: 'Склад',
  CHINA: 'Китай',
  RF: 'РФ',
  MANUFACTURER: 'Производители',
  OTHER: 'Другое',
}

function formatAgo(s: number): string {
  if (s < 60) return `${s} сек назад`
  if (s < 3600) return `${Math.floor(s / 60)} мин назад`
  return `${Math.floor(s / 3600)} ч назад`
}

export interface ProcurementHeaderProps {
  userName: string
  primaryMatrix: string | null
  period: ProcurementPeriodKey
  onPeriodChange: (k: ProcurementPeriodKey) => void
  fetchedAt: number | null
}

export function ProcurementHeader({
  userName,
  primaryMatrix,
  period,
  onPeriodChange,
  fetchedAt,
}: ProcurementHeaderProps) {
  const wsStatus = useRealtimeStore((s) => s.status)
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [])

  const live = wsStatus === 'connected'
  const ago = fetchedAt ? Math.max(0, Math.floor((now - fetchedAt) / 1000)) : null
  const matrixLabel = primaryMatrix ? (MATRIX_RU[primaryMatrix] ?? primaryMatrix) : null

  return (
    <div className="flex items-start justify-between flex-wrap gap-3">
      <div>
        <h1 className="text-xl font-semibold text-gray-900">Моя работа · Закупщик</h1>
        <div className="text-xs text-gray-500 flex items-center gap-2 mt-0.5 flex-wrap">
          <span className="text-gray-700 font-medium">{userName}</span>
          {matrixLabel && (
            <>
              <span>·</span>
              <span>основная матрица: {matrixLabel}</span>
            </>
          )}
          {ago !== null && (
            <>
              <span>·</span>
              <span>Обновлено {formatAgo(ago)}</span>
            </>
          )}
          <span>·</span>
          <span
            className={`inline-flex items-center gap-1 ${
              live ? 'text-emerald-600' : 'text-gray-400'
            }`}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full ${live ? 'bg-emerald-500' : 'bg-gray-300'}`}
            />
            {live ? 'Live' : 'Offline'}
          </span>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="inline-flex rounded-xl border border-gray-200 bg-white overflow-hidden">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              onClick={() => onPeriodChange(p.key)}
              className={`px-3 py-1.5 text-sm transition-colors ${
                period === p.key ? 'bg-gray-900 text-white' : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <Link
          to="/procurement/queue"
          className="px-3 py-1.5 rounded-xl bg-brand-600 text-white text-sm hover:bg-brand-700 transition-colors"
        >
          К очереди →
        </Link>
      </div>
    </div>
  )
}
